/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import UI from "@mile-meeting/mile-ui";
import { logout } from "apis/auth/logout";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { member, userInfo } from "recoil/recoilState";
import { theme } from "styles/theme";
import Icon, { IconNames } from "uis/Icon";

const HeaderContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  height: 70px;
  background-color: white;
  align-items: center;
  padding: 12px 30px;
  box-shadow:
    0px 0px 1px 0px rgba(0, 0, 0, 0.08),
    0px 1px 2px 0px rgba(0, 0, 0, 0.12);
`;

const MenuContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
`;

const MenuIcon = styled.div`
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--global-color-blue-95);
  border-radius: 8px;
`;
const MenuTitle = styled.div`
  font-weight: 700;
  font-size: 20px;
  line-height: 28px;
  letter-spacing: -0.2px;
  color: var(--alias-color-label-strong);
`;

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const WorkspaceProfileContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
`;

const WorkspaceProfileImage = styled.div<{ src?: string }>`
  border-radius: 8px;
  width: 40px;
  height: 40px;
  background: ${(props) => `url("${props.src}")`};
  background-color: var(--component-color-fill-alternative);
  background-size: contain;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const WorkspaceProfileName = styled.div`
  font-weight: 700;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 0.14px;
  color: var(--alias-color-label-normal);
`;
const Line = styled.div`
  border-left: 1px solid var(--alias-color-line-normal);
  height: 20px;
`;

const PersonalProfileImage = styled.div<{ picture?: string | null }>`
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--component-color-fill-alternative);
  background-image: ${(props) => (props.picture ? `url("${props.picture}")` : "none")};
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
`;
const PersonalProfileName = styled.div`
  font-weight: 700;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 0.14px;
  color: var(--alias-color-label-normal);
`;

export default function Header() {
  const location = useLocation().pathname;
  const title = location.split("/")[1];
  const sub_section = decodeURIComponent(location.split("/")[2]);
  const memberInfo = useRecoilValue(member);
  const { user, workspace } = useRecoilValue(userInfo);
  const [isOpenLinkGoogleCalendarWarning, setIsOpenLinkGoogleCalendarWarning] = useState<boolean>(false);
  const [isOpenDetectionGoogleCalendarWarning, setIsOpenDetectingGoogleCalendarWarning] = useState<boolean>(false);

  useEffect(() => {
    if (user) {
      setIsOpenLinkGoogleCalendarWarning(
        !user.isPersonalCalendarConnected && !user.visitorsConnectPersonalCalendarModalDismissed
      );
      setIsOpenDetectingGoogleCalendarWarning(
        user.isPersonalCalendarConnected &&
          !user.visitorsCalendarDetectionEnabled &&
          !user.visitorsDetectPersonalCalendarModalDismissed
      );
    }
  }, [user]);

  const getHeaderTitle = () => {
    switch (title) {
      case "setting":
        return "설정";
      case "analytics":
        return "Mile Analytics";
      case "meeting":
        return "Mile Meeting";
      case "visitors":
        return "Mile Visitors";
    }
  };

  const getHeaderIcon = (): IconNames => {
    switch (title) {
      case "setting":
        return "setting";
      case "analytics":
        return "analytics";
      case "visitors":
        return "visitors";
      case "meeting":
        return "meeting";
      default:
        return "setting";
    }
  };

  return (
    <>
      <HeaderContainer>
        <MenuContainer>
          <MenuIcon>{title && <Icon name={getHeaderIcon()} color="blue" width={24} height={24} />}</MenuIcon>
          <MenuTitle>{getHeaderTitle()}</MenuTitle>
        </MenuContainer>
        <div
          css={css`
            display: flex;
            gap: 20px;
            align-items: center;
            margin-left: auto;
          `}
        >
          <ProfileContainer>
            <WorkspaceProfileContainer>
              <WorkspaceProfileImage src={memberInfo?.workspace?.image}>
                {!memberInfo?.workspace?.image && <MileLogo />}
              </WorkspaceProfileImage>
              <WorkspaceProfileName>{memberInfo?.workspace?.name}</WorkspaceProfileName>
              <Line />
              <PersonalProfileImage picture={memberInfo?.image}>
                {(!memberInfo?.image || memberInfo.image === "") && <MileLogo />}
              </PersonalProfileImage>
              <PersonalProfileName>{memberInfo?.nickname}</PersonalProfileName>
            </WorkspaceProfileContainer>
          </ProfileContainer>
          <UI.Button
            size="medium"
            type="outlined"
            primary={false}
            title="로그아웃"
            onClick={() => {
              logout();
              localStorage.clear();
              sessionStorage.clear();

              window.location.href = "/login";
            }}
          />
        </div>
      </HeaderContainer>
    </>
  );
}

const WarningContainer = styled.div<{ isOpen: boolean }>`
  border-bottom: 1px solid #e6ca81;
  border-top: 1px solid #e6ca81;
  background-color: #fdfbf1;
  display: ${(props) => (props.isOpen ? "flex" : "none")};
  height: 50px;
  align-items: center;
  padding: 0 24px;
  gap: 16px;
`;

const WarningTitle = styled.p`
  font-size: 13px;
  font-weight: 600;
`;

const WarningBtn = styled.p`
  text-decoration: underline;
  text-decoration-color: ${theme.neutral500};
  font-size: 13px;
  color: ${theme.neutral500};

  cursor: pointer;
  &:hover {
    font-weight: 800;
  }
`;

const WarningHide = styled.p`
  font-size: 13px;
  font-weight: 500px;
  text-decoration: underline;
  text-decoration-color: ${theme.neutral500};
  color: ${theme.neutral500};
  margin-left: auto;

  cursor: pointer;
  &:hover {
    font-weight: 700;
  }
`;

type TWarningBarProps = {
  title?: string;
  btnLabel?: string;
  isOpen?: boolean;
  onClose?: () => void;
  onHandleBtn?: () => void;
  onHandleHide?: () => void;
};
const WarningBar = (props: TWarningBarProps) => {
  const { title, btnLabel, isOpen = false, onClose, onHandleBtn, onHandleHide } = props;
  return (
    <WarningContainer isOpen={isOpen}>
      <WarningTitle>{title}</WarningTitle>
      <WarningBtn
        onClick={onHandleBtn}
        css={css`
          font-size: 13px;
          font-weight: 600;
          color: ${theme.primary500};

          &:hover {
            font-weight: 800;
            cursor: pointer;
          }
        `}
      >
        {btnLabel}
      </WarningBtn>
      <WarningHide onClick={onHandleHide}>다시보지 않기</WarningHide>
      <div
        onClick={onClose}
        css={css`
          cursor: pointer;
          align-items: center;
          display: flex;
        `}
      >
        <Icon name={"close"} />
      </div>
    </WarningContainer>
  );
};

const MileLogo = () => {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g id="Mile Logo">
        <path
          id="Subtract"
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M16.0645 4.87231H9.49351C7.03312 4.87231 5.03857 6.86685 5.03857 9.32722V18.6825C5.03857 21.1429 7.03312 23.1374 9.49351 23.1374H18.8489C19.6915 23.1374 20.4796 22.9035 21.1515 22.497L19.369 20.3874C19.2045 20.4375 19.0298 20.4645 18.8489 20.4645H9.49351C8.50935 20.4645 7.71154 19.6667 7.71154 18.6825V9.32722C7.71154 8.34307 8.50935 7.54526 9.49351 7.54526H16.0645V4.87231ZM18.8489 7.54526C19.833 7.54526 20.6308 8.34307 20.6308 9.32722V18.6825C20.6308 18.8155 20.6163 18.9451 20.5886 19.0698L22.4629 21.288C22.992 20.5553 23.3038 19.6553 23.3038 18.6825V9.32722C23.3038 6.86685 21.3093 4.87231 18.8489 4.87231V7.54526Z"
          fill="#474C52"
          fill-opacity="0.35"
        />
        <path
          id="Subtract_2"
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M5.15074 3.53574C4.96979 3.53574 4.79514 3.56271 4.6306 3.61285L2.84809 1.50326C3.52005 1.09675 4.30807 0.862793 5.15074 0.862793H14.5061C16.9665 0.862793 18.961 2.85732 18.961 5.3177V14.673C18.961 17.1334 16.9665 19.1279 14.5061 19.1279H7.60095V16.455H14.5061C15.4903 16.455 16.2881 15.6572 16.2881 14.673V5.3177C16.2881 4.33355 15.4903 3.53574 14.5061 3.53574H5.15074ZM4.81662 16.4237C3.99217 16.2673 3.36876 15.543 3.36876 14.673V5.3177C3.36876 5.1847 3.38333 5.0551 3.41097 4.93041L1.53673 2.71227C1.00761 3.44493 0.695801 4.3449 0.695801 5.3177V14.673C0.695801 17.021 2.51228 18.9447 4.81662 19.1156V16.4237Z"
          fill="#474C52"
          fill-opacity="0.35"
        />
      </g>
    </svg>
  );
};
